<template>
  <div style="display: inline-block">
    <v-btn v-if="isAdd" small color="primary" outlined @click.stop="dialog = !dialog">
      <v-icon left small>add</v-icon> Add Catalog
    </v-btn>
    <v-btn v-else @click.stop="dialog = !dialog" color="secondary">edit</v-btn>
    <v-dialog
      v-if="dialog"
      v-model="dialog"
      scrollable
      max-width="700"
      transition="dialog-bottom-transition"
      @click:outside="resetForm"
    >
      <v-card>
        <v-card-title class="grey lighten-4 py-4 title black--text">
          <div>{{ isAdd ? 'Add' : 'Edit' }} Discount Package Catalog</div>
          <v-spacer></v-spacer>
          <v-card-actions v-if="!isAdd">
            <v-btn icon @click="deleteCatalog"><v-icon color="red">delete</v-icon></v-btn>
          </v-card-actions>
        </v-card-title>
        <v-card-text>
          <v-form v-model="valid" ref="catalogForm">
            <v-container grid-list-xs>
              <v-layout row wrap>
                <!-- Code -->
                <v-flex xs12>
                  <v-text-field
                    label="Code *"
                    v-model="form.code"
                    :rules="[(v) => !!v || 'Code is required']"
                    required
                    hint="e.g. student, first_jobber, manager"
                    persistent-hint
                  />
                </v-flex>

                <!-- Name -->
                <v-flex xs6>
                  <v-text-field
                    label="Name (TH) *"
                    v-model="form.name_th"
                    :rules="[(v) => !!v || 'Required']"
                    required
                  />
                </v-flex>
                <v-flex xs6>
                  <v-text-field
                    label="Name (EN) *"
                    v-model="form.name_en"
                    :rules="[(v) => !!v || 'Required']"
                    required
                  />
                </v-flex>

                <!-- Description -->
                <v-flex xs6>
                  <v-textarea label="Description (TH)" v-model="form.description_th" rows="3" />
                </v-flex>
                <v-flex xs6>
                  <v-textarea label="Description (EN)" v-model="form.description_en" rows="3" />
                </v-flex>

                <!-- Theme -->
                <v-flex xs12>
                  <div class="text-subtitle-2 mb-2">Theme</div>
                </v-flex>
                <v-flex xs6>
                  <v-text-field
                    label="Gradient Start Color"
                    v-model="form.gradient_start"
                    hint="e.g. #8AD866"
                    persistent-hint
                  />
                </v-flex>
                <v-flex xs6>
                  <v-text-field
                    label="Gradient End Color"
                    v-model="form.gradient_end"
                    hint="e.g. #25CD86"
                    persistent-hint
                  />
                </v-flex>
                <v-flex xs6>
                  <v-text-field label="Badge Color" v-model="form.badge_color" hint="e.g. #F9A400" persistent-hint />
                </v-flex>
                <v-flex xs6>
                  <v-select
                    :items="statusOptions"
                    label="Status"
                    v-model="form.status"
                    item-text="text"
                    item-value="value"
                  />
                </v-flex>

                <!-- Accepted Providers -->
                <v-flex xs12>
                  <v-autocomplete
                    v-model="form.provider_ids"
                    :items="providerItems"
                    :search-input.sync="providerSearch"
                    :loading="providerLoading"
                    :item-text="providerLabel"
                    item-value="id"
                    label="Accepted Providers"
                    hint="Leave empty to allow all providers"
                    persistent-hint
                    multiple
                    chips
                    deletable-chips
                    no-filter
                    cache-items
                  />
                </v-flex>
                <v-flex xs6>
                  <div class="text-caption mb-1">Home Card Background</div>
                  <div class="d-flex align-center">
                    <img
                      v-if="selectHomeCardBgImageBase64 || form.home_card_bg_image"
                      :src="selectHomeCardBgImageBase64 || form.home_card_bg_image"
                      class="mr-4"
                      style="max-width: 120px; max-height: 60px"
                    />
                    <v-btn
                      fab
                      dark
                      small
                      color="cyan"
                      elevation="1"
                      @click="() => openImagePicker('home_card_bg_image')"
                    >
                      <v-icon dark>edit</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-caption mt-1">76×76pt — small square thumbnail on the home page (SVG, max 1MB)</div>
                </v-flex>
                <v-flex xs6>
                  <div class="text-caption mb-1">Card Background</div>
                  <div class="d-flex align-center">
                    <img
                      v-if="selectCardBgImageBase64 || form.card_bg_image"
                      :src="selectCardBgImageBase64 || form.card_bg_image"
                      class="mr-4"
                      style="max-width: 120px; max-height: 60px"
                    />
                    <v-btn fab dark small color="cyan" elevation="1" @click="() => openImagePicker('card_bg_image')">
                      <v-icon dark>edit</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-caption mt-1">327×112pt — wide banner on the package list card (SVG, max 1MB)</div>
                </v-flex>
                <v-flex xs6>
                  <div class="text-caption mb-1">Banner Background (TH)</div>
                  <div class="d-flex align-center">
                    <img
                      v-if="selectBannerBgImageBase64 || form.banner_bg_image"
                      :src="selectBannerBgImageBase64 || form.banner_bg_image"
                      class="mr-4"
                      style="max-width: 120px; max-height: 60px"
                    />
                    <v-btn fab dark small color="cyan" elevation="1" @click="() => openImagePicker('banner_bg_image')">
                      <v-icon dark>edit</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-caption mt-1">
                    393×320pt — hero banner on the package detail page, with Thai text (SVG, max 1MB). Also the fallback
                    for any language with no upload of its own, so put the Thai artwork here — not the English one.
                    Leave empty to show the gradient with the catalog's own name and description instead.
                  </div>
                </v-flex>
                <v-flex xs6>
                  <div class="text-caption mb-1">Banner Background (EN)</div>
                  <div class="d-flex align-center">
                    <img
                      v-if="selectBannerEnBgImageBase64 || form.banner_en_bg_image"
                      :src="selectBannerEnBgImageBase64 || form.banner_en_bg_image"
                      class="mr-4"
                      style="max-width: 120px; max-height: 60px"
                    />
                    <v-btn
                      fab
                      dark
                      small
                      color="cyan"
                      elevation="1"
                      @click="() => openImagePicker('banner_en_bg_image')"
                    >
                      <v-icon dark>edit</v-icon>
                    </v-btn>
                  </div>
                  <div class="text-caption mt-1">
                    Same 393×320pt banner with English text — shown when the app language is English. Optional; the Thai
                    banner is used when this is empty.
                  </div>
                </v-flex>
                <input
                  type="file"
                  ref="imageInput"
                  style="display: none"
                  accept="image/*,.svg"
                  @change="onSelectFile"
                />

                <v-flex xs12>
                  <v-card-actions>
                    <v-btn color="accent" :disabled="!valid" @click="submit">
                      {{ isAdd ? 'Create' : 'Update' }}
                    </v-btn>
                    <v-btn text @click="closeDialog">Cancel</v-btn>
                  </v-card-actions>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import discountPackage from '../../../services/discountPackage';
import providerProfiles from '../../../services/providerProfiles';
import { srcToBase64 } from '../../../utils/stringUtil';

export default {
  name: 'AddOrEditDiscountPackage',
  props: {
    isAdd: { type: Boolean, default: true },
    catalog: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      dialog: false,
      valid: false,
      form: this.emptyForm(),
      statusOptions: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      currentImageType: null,
      selectHomeCardBgImageFileName: null,
      selectHomeCardBgImageBase64: null,
      selectBannerBgImageFileName: null,
      selectBannerBgImageBase64: null,
      selectBannerEnBgImageFileName: null,
      selectBannerEnBgImageBase64: null,
      selectCardBgImageFileName: null,
      selectCardBgImageBase64: null,
      providerItems: [],
      providerSearch: null,
      providerLoading: false,
      providerSearchTimeout: null,
    };
  },
  methods: {
    emptyForm() {
      return {
        code: '',
        name_th: '',
        name_en: '',
        description_th: '',
        description_en: '',
        gradient_start: '',
        gradient_end: '',
        badge_color: '',
        home_card_bg_image: '',
        banner_bg_image: '',
        banner_en_bg_image: '',
        card_bg_image: '',
        status: 'active',
        provider_ids: [],
      };
    },
    providerLabel(item) {
      if (item.fullNameTH || item.fullName) {
        return item.fullNameTH || item.fullName;
      }
      // Items seeded from catalog.accepted_providers ({firstname/lastname: {th, en}})
      const firstname = item.firstname || {};
      const lastname = item.lastname || {};
      const th = `${firstname.th || ''} ${lastname.th || ''}`.trim();
      const en = `${firstname.en || ''} ${lastname.en || ''}`.trim();
      return th || en || `#${item.id}`;
    },
    async searchProviders(q) {
      this.providerLoading = true;
      try {
        const params = { per_page: 20, no_cache: true };
        if (q) params.q = q;
        const result = await providerProfiles.all(params);
        const found = result.data || [];
        // Keep already-selected entries so their chips don't lose labels
        const selected = this.providerItems.filter((item) => this.form.provider_ids.includes(item.id));
        const merged = [...selected];
        found.forEach((item) => {
          if (!merged.find((m) => m.id === item.id)) merged.push(item);
        });
        this.providerItems = merged;
      } catch (error) {
        console.error('Failed to search providers:', error);
        const msg = error?.response?.data?.error?.message || 'Failed to load providers.';
        this.$notify({ title: 'Error', text: msg, type: 'error' });
      } finally {
        this.providerLoading = false;
      }
    },
    fillForm() {
      if (!this.catalog) return;
      const name = this.catalog.name || {};
      const desc = this.catalog.description || {};
      const theme = this.catalog.theme || {};
      const gradient = Array.isArray(theme.gradient) ? theme.gradient : [];
      this.form = {
        code: this.catalog.code || '',
        name_th: name.th || '',
        name_en: name.en || '',
        description_th: desc.th || '',
        description_en: desc.en || '',
        gradient_start: gradient[0] || '',
        gradient_end: gradient[1] || '',
        badge_color: theme.badgeColor || '',
        home_card_bg_image: theme.homeCardBackgroundImage || '',
        banner_bg_image: theme.bannerBackgroundImage || '',
        banner_en_bg_image: theme.bannerBackgroundImageEn || '',
        card_bg_image: theme.cardBackgroundImage || '',
        status: this.catalog.status || 'active',
        provider_ids: (this.catalog.accepted_providers || []).map((p) => p.id),
      };
      this.providerItems = this.catalog.accepted_providers || [];
      this.selectHomeCardBgImageFileName = null;
      this.selectHomeCardBgImageBase64 = null;
      this.selectBannerBgImageFileName = null;
      this.selectBannerBgImageBase64 = null;
      this.selectBannerEnBgImageFileName = null;
      this.selectBannerEnBgImageBase64 = null;
      this.selectCardBgImageFileName = null;
      this.selectCardBgImageBase64 = null;
    },
    resetForm() {
      this.form = this.emptyForm();
      this.providerItems = [];
      this.providerSearch = null;
      this.selectHomeCardBgImageFileName = null;
      this.selectHomeCardBgImageBase64 = null;
      this.selectBannerBgImageFileName = null;
      this.selectBannerBgImageBase64 = null;
      this.selectBannerEnBgImageFileName = null;
      this.selectBannerEnBgImageBase64 = null;
      this.selectCardBgImageFileName = null;
      this.selectCardBgImageBase64 = null;
      if (this.$refs.catalogForm) this.$refs.catalogForm.resetValidation();
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
    openImagePicker(type) {
      this.currentImageType = type;
      this.$refs.imageInput.click();
    },
    onSelectFile(e) {
      const files = e.target.files;
      const file = files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result;
        if (this.currentImageType === 'home_card_bg_image') {
          this.selectHomeCardBgImageFileName = file.name;
          this.selectHomeCardBgImageBase64 = base64String;
        } else if (this.currentImageType === 'banner_bg_image') {
          this.selectBannerBgImageFileName = file.name;
          this.selectBannerBgImageBase64 = base64String;
        } else if (this.currentImageType === 'banner_en_bg_image') {
          this.selectBannerEnBgImageFileName = file.name;
          this.selectBannerEnBgImageBase64 = base64String;
        } else if (this.currentImageType === 'card_bg_image') {
          this.selectCardBgImageFileName = file.name;
          this.selectCardBgImageBase64 = base64String;
        }
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    },
    buildPayload() {
      const homeCardBackgroundImage = this.selectHomeCardBgImageBase64
        ? {
            image_base64: srcToBase64(this.selectHomeCardBgImageBase64),
            image_filename: this.selectHomeCardBgImageFileName,
          }
        : this.form.home_card_bg_image;
      const bannerBackgroundImage = this.selectBannerBgImageBase64
        ? {
            image_base64: srcToBase64(this.selectBannerBgImageBase64),
            image_filename: this.selectBannerBgImageFileName,
          }
        : this.form.banner_bg_image;
      const bannerBackgroundImageEn = this.selectBannerEnBgImageBase64
        ? {
            image_base64: srcToBase64(this.selectBannerEnBgImageBase64),
            image_filename: this.selectBannerEnBgImageFileName,
          }
        : this.form.banner_en_bg_image;
      const cardBackgroundImage = this.selectCardBgImageBase64
        ? { image_base64: srcToBase64(this.selectCardBgImageBase64), image_filename: this.selectCardBgImageFileName }
        : this.form.card_bg_image;
      return {
        code: this.form.code,
        name: { th: this.form.name_th, en: this.form.name_en },
        description: { th: this.form.description_th, en: this.form.description_en },
        theme: {
          gradient: [this.form.gradient_start, this.form.gradient_end],
          badgeColor: this.form.badge_color,
          homeCardBackgroundImage,
          bannerBackgroundImage,
          bannerBackgroundImageEn,
          cardBackgroundImage,
        },
        status: this.form.status,
        provider_ids: this.form.provider_ids,
      };
    },
    async submit() {
      if (!this.valid) return;
      const payload = this.buildPayload();
      try {
        if (this.isAdd) {
          await discountPackage.create(payload);
          this.$notify({ title: 'Success', text: 'Discount package catalog created.', type: 'success' });
        } else {
          await discountPackage.update(this.catalog.id, payload);
          this.$notify({ title: 'Success', text: 'Discount package catalog updated.', type: 'success' });
        }
        this.closeDialog();
        this.$emit('success');
      } catch (error) {
        const msg = error?.response?.data?.error?.message || 'Please try again.';
        this.$notify({ title: 'Error', text: msg, type: 'error' });
      }
    },
    async deleteCatalog() {
      const confirmed = await this.$root.$confirm(
        'Delete Catalog',
        `Are you sure you want to delete <b>${this.catalog.code}</b>? This action cannot be undone.`,
        { color: 'red' }
      );
      if (!confirmed) return;
      try {
        await discountPackage.deleteItem(this.catalog.id);
        this.$notify({ title: 'Success', text: 'Discount package catalog deleted.', type: 'success' });
        this.closeDialog();
        this.$emit('success');
      } catch (error) {
        this.$notify({ title: 'Error', text: 'Failed to delete catalog.', type: 'error' });
      }
    },
  },
  watch: {
    dialog(val) {
      if (val && !this.isAdd) {
        this.fillForm();
      }
      if (val) {
        // Preload the first page so the dropdown isn't empty before typing
        this.searchProviders('');
      }
    },
    providerSearch(q) {
      if (this.providerSearchTimeout) clearTimeout(this.providerSearchTimeout);
      const query = (q || '').trim();
      this.providerSearchTimeout = setTimeout(() => this.searchProviders(query), 400);
    },
  },
};
</script>
